import { CheckIcon } from '@chakra-ui/icons'
import { Box, Button, Checkbox, Flex, Text, TooltipProps } from '@chakra-ui/react'
import { ScreenContext, ThemeContext, ThemeContextData, useCachedUser, useMe } from '@hb/react'
import {
  AssessmentDocument,
  colors,
  ConsentForm,
  FieldMap,
  getDateTimeString,
  getFieldsFromText,
  getFullName,
  isConsentForm,
  isField,
  isSignableDocument,
  populateSignatureFields,
  SignableDocument,
  TemplateNodeFieldMap,
  UploadProgress,
  WithId,
} from '@hb/shared'
import arrayMutators from 'final-form-arrays'
import {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Form, useFormState } from 'react-final-form'
import { useSlate } from 'slate-react'
import { SolidActionButton } from '../Buttons'
import { CollapseError } from '../CollapseError'
import { useFormSubmit } from '../forms/FinalForm/hooks/useFormSubmit'
import { useFormValidate } from '../forms/FinalForm/hooks/useFormValidate'
import { OnFormSubmit } from '../forms/FinalForm/types'
import { UploadProgressView } from '../forms/FinalForm/UploadProgressView'
import { InputRef } from '../forms/Input/types'
import { useElectronicConsentOverlay } from '../Users/Profile/ConsentForms/ElectronicConsent/context'
import {
  DocumentFormContext,
  DocumentFormContextValue,
  useDocumentForm,
  useTextEditor,
} from './context'
import { DocumentFormTermsModal } from './DocumentFormTermsModal'
import { DocumentFormTroubleSigningOverlay } from './DocumentFormTroubleSigningModal'

const DisclaimerCheckbox = () => {
  const { disclaimerChecked, setDisclaimerChecked, termsOpen, setTermsOpen } = useDocumentForm()
  return (
    <Flex align="center" justify="flex-end" px={2}>
      <Checkbox
        colorScheme="green"
        sx={{
          WebkitTapHighlightColor: 'transparent',
        }}
        mr={1.5}
        fontWeight={500}
        isChecked={disclaimerChecked}
        onChange={() => setDisclaimerChecked(!disclaimerChecked)}>
        I agree
      </Checkbox>
      <Text whiteSpace="nowrap">to the</Text>&nbsp;
      <Button
        position="relative"
        bottom="1px"
        variant="link"
        display="inline"
        width="auto"
        whiteSpace="nowrap"
        textDecoration="underline"
        onClick={() => setTermsOpen(!termsOpen)}>
        {' '}
        electronic signature terms
      </Button>
    </Flex>
  )
}
const TextEditorFormFooter = ({
  uploads,
  field,
  document,
  handleSubmit,
  onTroubleSigningClick,
}: {
  uploads: Record<string, UploadProgress> | null
  field: FieldMap
  document?: AssessmentDocument | SignableDocument
  handleSubmit: () => void
  onTroubleSigningClick: () => void
}) => {
  const { submitting, errors } = useFormState({
    subscription: { submitting: true, errors: true },
  })

  const { isMobile } = useContext(ScreenContext)
  const { fieldsDisabled: readOnly, disclaimerChecked } = useDocumentForm()

  const firstErrorText = useMemo(() => {
    const [firstError] = Object.keys(errors ?? {})
    if (!firstError) return ''
    const firstErrorField = field.children[firstError]
    return firstErrorField && isField(firstErrorField)
      ? `${firstErrorField.placeholder}: ${errors?.[firstError]}`
      : `Error: ${errors?.[firstError]}`
  }, [errors, field])

  const { data: signedByUser } = useCachedUser((document as ConsentForm)?.signedBy ?? null)
  const { data: awaitingSignatureByUser } = useCachedUser(
    (document as ConsentForm)?.toBeSignedByUser ?? null,
  )

  const statusText = useMemo(() => {
    if (!document || !isSignableDocument(document)) return ''
    if (document.signedOn)
      return `Signed at ${getDateTimeString(document.signedOn)} by ${getFullName(signedByUser)}`
    if (document.toBeSignedByUser)
      return `Awaiting signature from ${getFullName(awaitingSignatureByUser)}`
    return 'Unsigned'
  }, [document, signedByUser, awaitingSignatureByUser])
  const isUnsignedConsentForm = useMemo(() => {
    if (!document || !isSignableDocument(document)) return false
    return !document.signedOn && document.toBeSignedByUser
  }, [document])

  return (
    <Flex bg="gray.50" borderTop="1px solid #cdcdcd" w="100%" direction="column">
      <UploadProgressView uploads={uploads} />
      <Flex flexFlow={isMobile ? 'column' : 'row'} align="center" gap={2} w="100%" py={2} px={3}>
        {isUnsignedConsentForm && !readOnly ? (
          <Box w={isMobile ? '100%' : 'auto'} px={2} minW="0">
            <Button
              variant="outline"
              size="sm"
              color="gray.700"
              bg="white"
              _dark={{ bg: 'gray.900' }}
              onClick={onTroubleSigningClick}
              whiteSpace="nowrap">
              Having trouble signing your form?
            </Button>
          </Box>
        ) : null}
        {readOnly ? (
          <Flex align="center" gap={1}>
            {document && isSignableDocument(document) && document.signedOn ? (
              <CheckIcon color={colors.green.hex} />
            ) : null}
            <Text color={colors.green.hex} fontFamily="Open Sans" fontWeight={600} fontSize="sm">
              {statusText}
            </Text>
          </Flex>
        ) : (
          <Flex
            alignItems="center"
            width={isMobile ? '100%' : 'auto'}
            flex={isMobile ? 'unset' : 1}
            minW="0">
            <Box flex={1} minW="0" px={2}>
              {firstErrorText ? (
                <CollapseError inverted textAlign="right" error={firstErrorText} />
              ) : (
                <DisclaimerCheckbox />
              )}
            </Box>
            <SolidActionButton
              type="submit"
              pointerEvents={firstErrorText || !disclaimerChecked ? 'none' : 'auto'}
              opacity={firstErrorText || !disclaimerChecked ? 0.5 : 1}
              isLoading={submitting}
              onClick={handleSubmit}>
              Submit
            </SolidActionButton>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

const textEditorTheme: ThemeContextData<TooltipProps> = {
  theme: 'detailed',
  placeholderAbove: false,
  tooltipError: true,
  errorTooltipProps: {
    placement: 'bottom',
    hasArrow: false,
    offset: [0, -4],
    fontSize: 'xs',
    px: '6px',
    borderWidth: '1px',
    borderColor: '#cdcdcd',
    py: '2px',
  },
}

// export type OnSubmitForm = (
//   submitted: any,
//   onUploadProgress: (progress: Record<string, UploadProgress>) => void,
//   form: FormApi,
// ) => Promise<ValidationErrors>

export interface DocumentFormProps {
  readOnly?: boolean
  document?: WithId<AssessmentDocument> | WithId<SignableDocument>
  height: number
  width: number
  modalWidth?: number
  onFormSubmit?: OnFormSubmit
  style?: CSSProperties
}

export const DocumentFormProvider = ({
  children,
  document,
  style,
  onFormSubmit: onSubmit,
  height,
  width,
  field,
  // readOnly,
}: PropsWithChildren<DocumentFormProps & { field: FieldMap }>) => {
  const initData = useMemo(
    () => (document && isSignableDocument(document) ? (document.formData ?? {}) : {}),
    [document],
  )

  const { mode } = useTextEditor()
  const [termsOpen, setTermsOpen] = useState(false)
  const [troubleSigningOpen, setTroubleSigningOpen] = useState(false)
  const { isOpen: isConsentModalOpen } = useElectronicConsentOverlay()
  const [disclaimerChecked, setDisclaimerChecked] = useState(false)
  const [uploads, setUploads] = useState<Record<string, UploadProgress> | null>(null)
  const inputs = useRef<Record<string, { input: InputRef; container: HTMLDivElement }> | null>(null)
  const registerInput = useCallback(
    (id: string, ref: InputRef | null, container: HTMLDivElement | null) => {
      if (!ref || !container) return
      inputs.current = { ...inputs.current, [id]: { input: ref, container } }
    },
    [],
  )
  const unregisterInput = useCallback((id: string) => {
    delete inputs.current?.[id]
  }, [])

  const me = useMe()
  const { toBeSignedById, toBeSignedByMe } = useMemo(() => {
    if (!document || !isSignableDocument(document)) {
      return { toBeSignedById: null, toBeSignedByMe: false }
    }
    return {
      toBeSignedById: document.toBeSignedByUser,
      toBeSignedByMe: document.toBeSignedByUser === me?.uid,
    }
  }, [document, me])

  const { data: toBeSignedByUser, loading: signerLoading } = useCachedUser(toBeSignedById)

  const populatedField = useMemo(() => {
    if (!document || !isSignableDocument(document) || !toBeSignedByUser) return field
    return populateSignatureFields(field, toBeSignedByUser)
  }, [field, document, toBeSignedByUser])
  const validate = useFormValidate(populatedField)
  const usedValidate = useMemo(() => {
    if (!document || !isSignableDocument(document)) return undefined
    return document.signedOn ? undefined : validate
  }, [validate, document])

  // submit and close terms if success
  const handleSubmit = useCallback<OnFormSubmit>(
    async (...args) => {
      if (!disclaimerChecked) return
      if (!onSubmit) return
      const res = await onSubmit(...args)
      setTermsOpen(false)
      return res
    },
    [disclaimerChecked, setTermsOpen, onSubmit],
  )

  const { onFormSubmit } = useFormSubmit(populatedField, handleSubmit, setUploads)

  const getInputById = useCallback((id: string) => inputs.current?.[id]?.input ?? null, [])

  const goNextById = useCallback(
    (id: string) => {
      const input = getInputById(id)
      if (!input) return false
      const wentNext = input.toNext?.() ?? false
      return wentNext
    },
    [getInputById],
  )

  const focusInput = useCallback((id: string) => {
    // form.focus(id)
    const inputRefs = inputs.current?.[id]
    if (!inputRefs) return
    inputRefs.input.focus()
    inputRefs.container.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const contextData = useMemo<DocumentFormContextValue>(
    () => ({
      field: populatedField,
      fieldsDisabled: !onSubmit || mode === 'Edit' || !toBeSignedByMe || troubleSigningOpen,
      troubleSigningOpen,
      toBeSignedByUser,
      document,
      signerLoading,
      toBeSignedByMe: toBeSignedByMe && !isConsentModalOpen,
      disclaimerChecked,
      setDisclaimerChecked,
      termsOpen,
      setTermsOpen,
      width,
      height,
      registerInput,
      unregisterInput,
      focusInput,
      getInputById,
      goNextById,
    }),
    [
      populatedField,
      onSubmit,
      toBeSignedByUser,
      signerLoading,
      document,
      toBeSignedByMe,
      mode,
      troubleSigningOpen,
      disclaimerChecked,
      setDisclaimerChecked,
      termsOpen,
      setTermsOpen,
      getInputById,
      width,
      height,
      isConsentModalOpen,
      registerInput,
      unregisterInput,
      focusInput,
      goNextById,
    ],
  )
  const { isMobile } = useContext(ScreenContext)
  const usedHeight = useMemo(() => (typeof height === 'number' ? `${height}px` : height), [height])
  const theme = useMemo<ThemeContextData<TooltipProps>>(() => {
    const errorTooltipProps = isMobile
      ? { ...textEditorTheme.errorTooltipProps, isOpen: false }
      : textEditorTheme.errorTooltipProps
    if (isConsentModalOpen) return { ...textEditorTheme, tooltipError: false, errorTooltipProps }
    return { ...textEditorTheme, errorTooltipProps }
  }, [isConsentModalOpen, isMobile])
  return (
    <DocumentFormContext.Provider value={contextData}>
      <ThemeContext.Provider value={theme}>
        <Form
          mutators={{ ...arrayMutators }}
          onSubmit={onFormSubmit}
          initialValues={initData}
          validate={usedValidate}>
          {({ handleSubmit }) => (
            <Flex h={usedHeight} w="auto" direction="column" style={style}>
              <Box position="relative" minH="0" flex={1} w="auto">
                {children}
                {toBeSignedByMe ? <DocumentFormTermsModal /> : null}
                {document && isConsentForm(document) && toBeSignedByMe ? (
                  <DocumentFormTroubleSigningOverlay
                    isOpen={troubleSigningOpen}
                    onClose={() => setTroubleSigningOpen(false)}
                    document={document}
                    width={width}
                    height={height}
                  />
                ) : null}
              </Box>
              {onSubmit ? (
                <TextEditorFormFooter
                  document={document}
                  field={populatedField}
                  handleSubmit={handleSubmit}
                  uploads={uploads}
                  onTroubleSigningClick={() => setTroubleSigningOpen(true)}
                />
              ) : null}
            </Flex>
          )}
        </Form>
      </ThemeContext.Provider>
    </DocumentFormContext.Provider>
  )
}

export const TextEditorDocumentFormProvider = (props: PropsWithChildren<DocumentFormProps>) => {
  const { children: text } = useSlate()
  // if readonly, collect all fields from templateText
  const field = useMemo<TemplateNodeFieldMap>(
    () => ({
      name: '',
      children: getFieldsFromText(text),
    }),
    [text],
  )

  return <DocumentFormProvider field={field} {...props} />
}

export const PdfDocumentFormProvider = (props: PropsWithChildren<DocumentFormProps>) => {
  const { document } = props
  const field = useMemo<FieldMap>(
    () =>
      document && isSignableDocument(document)
        ? {
            name: '',
            children: Object.values(document?.externalPdf?.attachments ?? {}).reduce<
              FieldMap['children']
            >((acc, attachment) => {
              if (attachment.type === 'input') {
                return {
                  ...acc,
                  [attachment.id]: attachment.field,
                }
              }
              return acc
            }, {}),
          }
        : {
            name: '',
            children: {},
          },
    [document],
  )
  return <DocumentFormProvider field={field} {...props} />
}
